import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'

import AddShiftContextualButton from 'modules/shift_management/components/ShiftManagement/AddShiftContextualButton'
import DropItem from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CellActions/EmptyCellActions'
import BaseShift from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/ShiftCell/BaseShift'
import WrapperCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/WrapperCell'
import EmptyShiftCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/EmptyShiftCell'
import { useDropShift } from 'modules/shift_management/hooks/useDragAndDrop'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import type { Shift, ShiftsRow, View } from 'modules/shift_management/lib/types'
import type DefaultShifts from 'modules/shift_management/store/internal/collections/DefaultShifts'
import { overrideGammaDimensions } from 'modules/shift_management/store/internal/models/View'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

interface Props {
  view: View
  shiftsRow: ShiftsRow
  shift: Shift
  preview: boolean
  previewPlannedBreaks?: PlannedBreak[] | null
  visibleDefaultShifts: DefaultShifts
}

const DailyShiftCell = ({
  view,
  shiftsRow,
  shift,
  preview,
  previewPlannedBreaks = null,
  visibleDefaultShifts,
}: Props) => {
  const { columnMinWidth, date } = view
  const { canManage } = shiftsRow

  // Calculate padding right and left to simulate partial hours in the grid
  const dimensions = overrideGammaDimensions()
  const minWidth = dimensions[columnMinWidth]
  const paddingLeft = `calc(${minWidth} * ${shift.leftOffset(date)})`
  const paddingRight = `calc(${minWidth} * ${shift.rightOffset(date)})`

  const { isOver, canDrop, drop } = useDropShift({
    shiftsRow,
    date,
    shift,
  })

  const { daysByRow, showEmptyShiftCell } = useSelectionState()

  const hourId = dateToString(shift.date, view.dateFormat)
  const isSelected = (daysByRow[shiftsRow.key] || []).includes(hourId)

  return (
    <WrapperCell minWidth={columnMinWidth} padding="s4" ref={drop}>
      <Box gap="s4">
        {/* Auxiliar box to simulate partial hours in the grid with custom padding */}
        <Box style={{ paddingLeft, paddingRight }}>
          <BaseShift
            date={shift.date}
            shiftsRow={shiftsRow}
            shift={shift}
            isNightShift={shift.isEndNightShift(date)}
            preview={preview}
            previewPlannedBreaks={previewPlannedBreaks}
          />
          {canManage && (
            <AddShiftContextualButton
              shiftsRow={shiftsRow}
              date={shift.date}
              visibleDefaultShifts={visibleDefaultShifts}
            />
          )}
        </Box>
        {showEmptyShiftCell && isSelected && <EmptyShiftCell shiftsRow={shiftsRow} />}
        {canDrop && isOver && (
          <DropItem
            date={shift.date}
            shiftsRow={shiftsRow}
            isOver={isOver}
            visibleDefaultShifts={visibleDefaultShifts}
          />
        )}
      </Box>
    </WrapperCell>
  )
}

export default observer(DailyShiftCell)
